import { css } from 'lit';

export const projectViewStyles = css`

  /*=== BASE LAYOUT STRUCTURE & STATE TRANSITION SETTINGS ===*/

  :host {
    overflow: hidden; /* prevents wonky scroll state when expanded on mobile */
  }

  view-content.loaded {
    display: flex;
    padding: 50px 0 0 0;
    max-width: none;

    --side-sheet-width: 15rem;
    --pv-transition-duration: 0.5s;
    --pv-transition: all var(--pv-transition-duration) ease-in-out;
  }
  @media (min-width: 1000px) {
    view-content.loaded {
      --side-sheet-width: 18rem;
    }
  }
  
  #side-sheet {
    flex: 0 0 var(--side-sheet-width);
    position: relative;

    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    
    padding: var(--view-item-spacing);
    transition: var(--pv-transition);

    margin-left: calc(-1 * (
      var(--side-sheet-width) + (2 * var(--view-item-spacing))
    ));
  }
  #side-sheet.open {
    margin-left: 0;
  }

  #main-sheet {
    transition: var(--pv-transition);
    flex: 1 1 auto;
    position: relative;

    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    min-height: 90vh;

    padding: var(--view-item-spacing);
    padding-left: 0;

    --mc-button-scale: 0;
    --mc-avatar-scale: 0;
    --mc-avatar-max-width: 0;
    --mc-header-bar-gap: 0;
  }
  #main-sheet.expanded {
    padding-left: var(--view-item-spacing);

    --mc-button-scale: 1;
    --mc-avatar-scale: 1;
    --mc-avatar-max-width: 4.5rem;
    --mc-header-bar-gap: 1rem;
  }

  /*=== SIDE SHEET ===*/

  #side-sheet .controls {
    transition: var(--pv-transition);
    display: flex;
    justify-content: flex-end;
    
    position: absolute;
    left: 0;
    right: var(--view-item-spacing);
    z-index: 10;
  }
  #side-sheet .controls sl-icon-button {
    color: white;
    font-size: 2rem;
  }

  /*=== MAIN SHEET ===*/

  #main-sheet header-bar#title-bar {
    transition: var(--pv-transition);
    gap: var(--mc-header-bar-gap);
    flex-wrap: nowrap;

    position: relative;
    width: 100%;
    overflow: visible;
  }

  #main-sheet header-bar#title-bar sl-avatar#small-project-avatar {
    transition: var(--pv-transition);
    scale: var(--mc-avatar-scale);
    max-width: var(--mc-avatar-max-width);
  }
  sl-avatar#small-project-avatar::part(base) {
    border: 1px solid var(--theme-color-primary-3);
    box-shadow: var(--sl-shadow-large);;
  }

  #title-bar .controls {
    transition: var(--pv-transition);
    display: flex;
    justify-content: center;
    
    position: absolute;
    left: calc(-1.5 * var(--view-item-spacing));
    width: calc(1.5 * var(--view-item-spacing));
    z-index: 10;
  }
  #title-bar .controls sl-icon-button {
    transition: var(--pv-transition);
    color: white;
    font-size: 2rem;
    scale: var(--mc-button-scale);
  }

  #main-sheet button-bar#title-buttons {
    transition: var(--pv-transition);
    width: 100%;
    margin-bottom: -0.5rem !important;
  }

  /*=== MAIN SHEET MARGIN ALIGNMENT ===*/

  /** The max width constraint is always in place. */
  #main-sheet .main-sheet-margin-align,
  #main-sheet sl-tab-group#main-sheet-tabs::part(nav) {
    transition: var(--pv-transition);
    max-width: var(--q1-view-width);
  }

  /**
   * When the side sheet is open, the math is much more complex.
   * This is needed to get the main sheet to be centered and align with the app header.
   */
  #main-sheet.collapsed .main-sheet-margin-align,
  #main-sheet.collapsed sl-tab-group#main-sheet-tabs::part(nav) {
    margin-right: 0 !important;
    margin-left: max(0px, calc(
        ((100vw - var(--q1-view-width)) / 2)      /* half of total margin in app header */
        - var(--side-sheet-width)                 /* side sheet width */
        - (2 * var(--view-item-spacing))          /* side sheet left + right padding */
    )) !important;
  }

  /**
   * When full screen, the math is theoretically very simple. Auto margin on left and right will
   * match the app header and footer. But then the transitions are janky, so we need to actually
   * keep the manual calculation approach, but just remove the side sheet part.
   */
  #main-sheet.expanded .main-sheet-margin-align,
  #main-sheet.expanded sl-tab-group#main-sheet-tabs::part(nav) {
    margin-right: 0 !important;
    margin-left: max(0px, calc(
        ((100vw - var(--q1-view-width)) / 2)      /* half of total margin in app header */
        - (1 * var(--view-item-spacing))          /* main sheet padding (left only) */
    )) !important;
  }

  /*=== PROJECT HEADER (LEFT) ===*/

  sl-avatar#big-project-avatar {
    --size: var(--side-sheet-width);
  }
  sl-avatar#big-project-avatar::part(base) {
    border: 1px solid var(--theme-color-primary-3);
    box-shadow: var(--sl-shadow-large);;
  }

  .organizers {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  /*=== TAB PANEL ===*/

  sl-tab-group#main-sheet-tabs::part(nav) {
    width: 100%;

    --indicator-color: var(--theme-color-primary-4);
    --track-color: transparent;
    --track-width: 4px;
  }
  sl-tab-group#main-sheet-tabs::part(body) {
    overflow: visible;
  }

  /*=== PROJECT HEADER (RIGHT) ===*/

  header-bar#title-bar {
    margin-bottom: 0;
  }

  /* h1 {
    font-size: 1.5rem;
    line-height: 1.3em;
    font-weight: 500;
    margin: 0;
  }
  @media (min-width: 650px) {
    h1 {
      font-size: 2rem;
      line-height: 1.3em;
    }
  } */

  .project-header-info {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .project-header-info ul {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .project-header-info ul li {
    gap: 0.5rem;
    align-items: center;
    font-size: 1rem;
    line-height: 1em;
    opacity: 0.7;
    transition: opacity 0.2s;
  }
  .project-header-info ul li:not(.hide-at-bp2):not(.hide-until-bp2) {
    display: flex;
  }
  .project-header-info ul li:hover {
    opacity: 1;
  }

  sl-menu-item {
    font-style: normal;
  }

  .external-icon {
    font-size: 1.5rem;
    display: inline-flex;
    align-items: center;
    color: var(--dm-gray-6);
    vertical-align: middle;
  }

  .external-icon:hover {
    color: var(--dm-gray-7);
  }

  sl-button.external-icon-button::part(label) {
    padding: 0;
    margin: 0;
  }

  sl-button.external-icon-button::part(caret) {
    color: var(--dm-gray-6);
  }

  sl-button.external-icon-button:hover::part(caret) {
    color: var(--dm-gray-7);
  }

  sl-button.external-icon-button::part(base) {
    padding: 0;
  }

  .external-icon-link {
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    line-height: 1;
  }

  #project-headline {
    color: var(--dm-gray-6);
    font-size: 0.9rem;
    font-weight: 300;
  }

  /*=== SHARED STYLES ===*/

  sl-tab-panel h1 {
    font-size: 1.5rem;
    line-height: 1.3em;
    font-weight: 600;
  }

  h3 {
    font-size: 1.1rem;
    line-height: 1.3em;
    font-weight: 500;
    color: var(--gray-7);
  }

  h3.with-border {
    border-bottom: 1px solid var(--gray-3);
    padding-bottom: 0.75rem;
  }


  /*=== TAB CONTENT ===*/

  sl-tab-panel {
    --padding: var(--view-item-spacing) 0;

    --link-color-default: var(--theme-color-primary-6);
    --link-color-visited: var(--theme-color-primary-6);
    --link-color-focus: var(--theme-color-primary-8);
  }

  sl-tab-panel header-bar {
    margin-bottom: 0;
  }
  
  sl-tab-panel header-bar.with-border {
    padding-bottom: 1rem;
  }
  
  sl-tab-panel header-bar h1,
  sl-tab-panel header-bar h2,
  sl-tab-panel header-bar h3 {
    margin-bottom: 0;
  }

  sl-tab-panel q1-markdown {
    margin-top: var(--view-item-spacing);
  }

  sl-tab-panel empty-alert {
    margin-top: var(--view-item-spacing);
  }

  .organizers {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  sl-tab-panel q1-membership-list {
    margin-top: var(--view-item-spacing) !important;
  }

  /*=== BUTTON BARS ===*/

  button-bar {
    flex-wrap: nowrap;
    gap: 0.5rem;
  }
  button-bar sl-button.menu-button::part(base) {
    color: var(--dm-gray-7);
  }

  button-bar#title-buttons {
    flex: 0 0 auto;
    flex-wrap: wrap;
    justify-content: flex-end;
    
    width: 100%;
  }
  button-bar#title-buttons .info {
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: 0.35rem;

    color: var(--dm-gray-6);
    font-weight: 300;
    font-style: italic;
  }
  button-bar#title-buttons sl-button.menu-button::part(base) {
    padding-right: 0;
  }

  /*=== HOME TAB ===*/

  #log-items-list {
    display: flex;
    flex-direction: column;
    gap: var(--view-item-spacing);
    margin-top: var(--view-item-spacing);
  }

  .no-more-pages {
    padding: 1rem;
    width: 100%;
    text-align: center;
  }
  .no-more-pages span {
    margin: 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    gap: 0.5rem;
    color: var(--gray-7);
    font-style: italic;
    font-weight: 200;
  }

  avatar-stack {
    --avatar-border-color: var(--dm-gray-1);
    --avatar-stack-size: 1.6rem;
    margin-bottom: 0.5rem;
  }
  
`;